import React, { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppContext } from '../../../App'
import AddAlertIcon from '../../../assets/icons/AddAlertIcon'
import { ExtraSmallButton } from '../../custom'
import { addUserAlert } from '../../../reducer/alerts.reducer'
import {
    buildDrawingFromChartPoints,
    createUserAlertFromDrawing,
    getCreateUserAlertRequestObject,
    getUserAlertMessageFromCondition,
} from '../../../util/alerts'
import { createAlert } from '../../../services/apis/alerts'
import commonActions from '../../../actions/common.actions'
import { selectIndicators } from '../../../selectors/chart'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import type { LineDrawingSettings } from '../drawingTools/lineDrawingTool/lineDrawingTool'
import type { Point } from '../drawingTools/drawing.interface'

type DrawingAlertButtonProps = {
    drawingId: string
    chartId: string
    drawingType: string
    symbolName?: string
}

const DrawingAlertButton: React.FC<DrawingAlertButtonProps> = ({
    drawingId,
    chartId,
    drawingType,
    symbolName,
}) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const indicators = useSelector(selectIndicators)
    const { user: { hasAccess } } = useUser()

    const handleClick = useCallback(async () => {
        if (!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: 'Alerts',
                group: Group.CHART,
                featureRestrictionCode: FeatureCode.CHART_ALERTS,
            }))
            return
        }

        const chart = appContext.getChart(chartId)
        const getSnapshot = appContext.getChartSnapshot?.[chartId]
        const snapshot = getSnapshot ? getSnapshot() : null
        if (!chart || !snapshot) return

        const settings = chart.getSettingsValues(drawingId) as LineDrawingSettings | undefined
        const point1 = settings?.point1 as Point | undefined
        const point2 = settings?.point2 as Point | undefined
        if (!point1?.x || point2?.x == null || point1.y == null || point2.y == null) return

        AnalyticsManager.logEvent('Add Drawing Alert Clicked', { source: 'drawing-toolbar', drawingType })

        try {
            const drawing = buildDrawingFromChartPoints(
                drawingId,
                drawingType,
                [point1, point2],
                chart.timezone
            )
            const baseAlert = createUserAlertFromDrawing(snapshot, drawing)
            const primary = snapshot.securityList?.[0]
            const security = primary
                ? { exchange: primary.exchange, code: primary.code }
                : baseAlert.security
            const generatedMessage = await getUserAlertMessageFromCondition(
                baseAlert.condition,
                symbolName || primary?.code || '',
                indicators,
                {
                    chartSnapshot: snapshot,
                    chartTimeBucket: snapshot.timeBucket ?? baseAlert.timeBucket,
                }
            )
            const alert = {
                ...baseAlert,
                security,
                message: generatedMessage || baseAlert.message,
            }
            const created = await createAlert(
                getCreateUserAlertRequestObject(alert, {
                    seriesGroups: chart.chartConfig.seriesGroups,
                    indicators,
                })
            )
            dispatch(addUserAlert(created))
        } catch (e: any) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: e?.message || 'Failed to create drawing alert',
                errorType: 'error',
            })
        }
    }, [appContext, chartId, drawingId, drawingType, dispatch, hasAccess, indicators, symbolName])

    return (
        <ExtraSmallButton onClick={handleClick} color="inherit" title="Add alert on drawing">
            <AddAlertIcon sx={{ fontSize: '1.25rem !important' }} />
        </ExtraSmallButton>
    )
}

export default React.memo(DrawingAlertButton)
